import PatientListSelectedIcon from "@assets/icons/patient-list.svg";
import PatientListIcon from "@assets/icons/patient-list.svg";
import TemplateSelectedIcon from "@assets/icons/template.svg";
import TemplateIcon from "@assets/icons/template.svg";
import LearnSelectedIcon from "@assets/icons/learnicon.svg";
import LearnIcon from "@assets/icons/learnicon.svg";
import DumbbellSelectedIcon from "@assets/icons/dumbbell.svg";
import DumbbellIcon from "@assets/icons/dumbbell.svg";
import { useI18n } from "@core/i18n/i18n";
import { useNavigate } from "@core/routing/use-navigate";
import { sendAnalyticsEventViewTemplateList } from "@domain/template/template-analytics";
import { e2eTestId } from "@e2e/test-ids";
import styled from "@emotion/styled";
import { WordingKey } from "@typings/i18n-typings";
import { AppLogo } from "@ui/components/common/app-logo";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { RoutesPath } from "@ui/navigation/routes";
import { BottomMobileMenuTab } from "@ui/navigation/tab-bar/bottom-mobile-menu-tab";
import { ProfileMenu } from "@ui/navigation/tab-bar/profile-menu";
import { physioMeMobileBreakpoint, row, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { Menubar } from "primereact/menubar";
import { TabPanel, TabView } from "primereact/tabview";
import { classNames } from "primereact/utils";
import React, { useCallback, useMemo } from "react";
import { matchRoutes, useLocation } from "react-router";
import { GetStartedTab } from "@ui/navigation/tab-bar/components/get-started-tab";
import { isNotNil } from "@utils/assert";
import { getStartedManager, learnManager } from "@core/service-container";
import { useObservable } from "micro-observables";

const tabKeys = ["getStarted", "patientListing", "exerciseListing", "templateListing", "learnListing"] as const;
type TabKey = (typeof tabKeys)[number];

type Tab = {
	index: number;
	route: RoutesPath;
	matchRoutes: RoutesPath[];
	label?: WordingKey;
	visible?: boolean;
	mobileVersion: (context: { index: number; selectedTabIndex: number }) => React.ReactElement;
	analyticsEvent?: () => void;
};

// mobile version renderers defined outside of TabBar to satisfy Sonar
const getStartedMobileVersion: Tab["mobileVersion"] = context => (
	<GetStartedTab isSelected={context.index === context.selectedTabIndex} />
);

const patientListingMobileVersion: Tab["mobileVersion"] = context => (
	<BottomMobileMenuTab
		context={context}
		label="tabbar.patients.mobile"
		icon={PatientListIcon}
		iconSelected={PatientListSelectedIcon}
	/>
);

const exerciseListingMobileVersion: Tab["mobileVersion"] = context => (
	<BottomMobileMenuTab
		context={context}
		label="tabbar.exercises.mobile"
		icon={DumbbellIcon}
		iconSelected={DumbbellSelectedIcon}
	/>
);

const templateListingMobileVersion: Tab["mobileVersion"] = context => (
	<BottomMobileMenuTab
		context={context}
		label="tabbar.programs.mobile"
		icon={TemplateIcon}
		iconSelected={TemplateSelectedIcon}
	/>
);

const learnListingMobileVersion: Tab["mobileVersion"] = context => (
	<BottomMobileMenuTab
		context={context}
		label="tabbar.learn.mobile"
		icon={LearnIcon}
		iconSelected={LearnSelectedIcon}
	/>
);

// desktop tabs component defined out of parent
interface DesktopTabsProps {
	selectedTab: number;
	tabs: Record<TabKey, Tab>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	format: (key: WordingKey, params?: any) => string;
	onTabChange: (index: number) => void;
}

const DesktopTabs: React.FC<DesktopTabsProps> = ({ selectedTab, tabs, format, onTabChange }) => (
	<StyledTabView className="p-size-m-bold" activeIndex={selectedTab} onTabChange={e => onTabChange(e.index)}>
		<TabPanel
			visible={tabs["getStarted"].visible}
			className={classNames(["mx-2", e2eTestId.homePage.tabGetStarted])}
			header={<GetStartedTab isSelected={selectedTab === tabs["getStarted"].index} />}
		/>
		<TabPanel
			headerClassName="patients"
			className={classNames(["mx-2", e2eTestId.homePage.tabPatient])}
			header={format("tabbar.patients")}
		/>
		<TabPanel
			className={classNames(["mx-2", e2eTestId.homePage.tabExercise])}
			header={format("tabbar.exercises")}
		/>
		<TabPanel
			className={classNames(["mx-2", e2eTestId.homePage.tabProgram])}
			header={format("tabbar.programs")}
		/>
		<TabPanel visible={tabs["learnListing"].visible} className={classNames(["mx-2"])} header={format("tabbar.learn")} />
	</StyledTabView>
);

export const TabBar: React.FC<{
	displayTabs?: boolean;
	children: React.ReactNode | JSX.Element;
}> = ({ displayTabs = true, children }) => {
	const navigate = useNavigate();
	const { format } = useI18n();
	const location = useLocation();
	const { isDesktop } = useDimensions();
	const shouldDisplayGetStartedTab = useObservable(getStartedManager.shouldDisplayGetStartedTab);
	const learnHasItems = useObservable(learnManager.hasItems);

	const tabs = useMemo<Record<TabKey, Tab>>(
		() => ({
			getStarted: {
				index: 0,
				route: RoutesPath.GetStarted,
				matchRoutes: [RoutesPath.GetStarted],
				visible: shouldDisplayGetStartedTab,
				mobileVersion: getStartedMobileVersion,
			},
			patientListing: {
				index: 1,
				route: RoutesPath.Home,
				matchRoutes: [RoutesPath.Home, RoutesPath.PatientDetails, RoutesPath.ProgramBuilder],
				label: "tabbar.patients",
				mobileVersion: patientListingMobileVersion,
			},
			exerciseListing: {
				index: 2,
				route: RoutesPath.ExerciseList,
				matchRoutes: [RoutesPath.ExerciseList],
				label: "tabbar.exercises",
				mobileVersion: exerciseListingMobileVersion,
			},
			templateListing: {
				index: 3,
				route: RoutesPath.ProgramList,
				matchRoutes: [RoutesPath.ProgramList, RoutesPath.TemplateBuilder],
				label: "tabbar.programs",
				analyticsEvent: sendAnalyticsEventViewTemplateList,
				mobileVersion: templateListingMobileVersion,
			},
			learnListing: {
				index: 4,
				route: RoutesPath.Learn,
				matchRoutes: [RoutesPath.Learn, RoutesPath.LearnDocument],
				label: "tabbar.learn",
				visible: learnHasItems,
				mobileVersion: learnListingMobileVersion,
			},
		}),
		[shouldDisplayGetStartedTab, learnHasItems],
	);

	const selectedTab = useMemo(() => {
		for (const tabKey of tabKeys) {
			if (
				matchRoutes(
					tabs[tabKey].matchRoutes.map(route => ({ path: route })),
					location,
				)
			) {
				return tabs[tabKey].index;
			}
		}
		return -1;
	}, [tabs, location]);

	const onTabChange = useCallback(
		(index: number) => {
			for (const tabKey of tabKeys) {
				const tab = tabs[tabKey];
				if (tab.index === index) {
					tab.analyticsEvent?.();
					navigate(tab.route);
					break;
				}
			}
		},
		[navigate, tabs],
	);

	const menuItems = useMemo(
		() => [
			...tabKeys.map(tabKey => {
				const tab = tabs[tabKey];
				return {
					className: "lg:hidden",
					label: isNotNil(tab.label) ? format(tab.label, undefined) : "",
					command: () => onTabChange(tab.index),
				};
			}),
			{
				className: "hidden lg:block",
				template: () => (
					<DesktopTabs
						selectedTab={selectedTab}
						tabs={tabs}
						format={format}
						onTabChange={onTabChange}
					/>
				),
			},

			{
				separator: true,
			},
		],
		[format, onTabChange, selectedTab, tabs],
	);

	const mobileMenuItems = useMemo(
		() =>
			isDesktop
				? []
				: tabKeys
						.map(tabKey => ({ ...tabs[tabKey], tabKey }))
						.sort((k1, k2) => k1.index - k2.index)
						.map(
							eachMenuItem =>
								eachMenuItem.visible !== false && (
									<BottomMobileMenuTabContainer
										key={eachMenuItem.tabKey}
										onClick={() => onTabChange(eachMenuItem.index)}
									>
										{eachMenuItem.mobileVersion({
											index: eachMenuItem.index,
											selectedTabIndex: selectedTab,
										})}
									</BottomMobileMenuTabContainer>
								),
						),
		[isDesktop, onTabChange, selectedTab, tabs],
	);

	return (
		<>
			{isDesktop ? (
				<>
					{displayTabs && (
						<StyledMenuBar
							className="lg:py-0"
							model={menuItems}
							start={<StyledAppLogo height={32} width={125} onClick={() => navigate(RoutesPath.Home)} />}
							end={<ProfileMenu />}
							displayTabs={displayTabs}
						/>
					)}
					{children}
				</>
			) : (
				<>
					{displayTabs && (
						<TopMobileMenuBar>
							<StyledAppLogo height={32} width={125} onClick={() => navigate(RoutesPath.Home)} />
							<ProfileMenu />
						</TopMobileMenuBar>
					)}
					{children}
					{displayTabs && <BottomMobileMenuBar>{mobileMenuItems.map(item => item)}</BottomMobileMenuBar>}
				</>
			)}
		</>
	);
};

const StyledMenuBar = styled(Menubar)<{ displayTabs: boolean }>`
	border-radius: 0;
	border-width: 0 0 1px 0;
	background-color: ${colors.white};

	.p-menubar-root-list {
		width: 100%;
		justify-content: center;
	}

	.p-menubar-button,
	.p-tabview {
		visibility: ${({ displayTabs }) => (displayTabs ? "visible" : "hidden")};
	}

	.p-tabview-title {
		line-height: 1.25em;
	}

	.p-menu-separator {
		flex: 1;
	}

	.p-menuitem-text {
		color: ${colors.grey} !important;
	}

	.p-menuitem-link * {
		font-weight: bold;
		font-size: 1.125rem;
	}
`;

const StyledAppLogo = styled(AppLogo)`
	cursor: pointer;
	margin-left: 40px;
	@media ${physioMeMobileBreakpoint} {
		margin: 0;
	}
`;

const StyledTabView = styled(TabView)`
	.p-tabview-panels {
		display: none;
	}

	.p-tabview-nav {
		display: flex;
		border-bottom: 2px solid ${colors.transparent};

		li .p-tabview-nav-link {
			margin-top: 0.75rem;
			padding: 0.75rem 0.5rem 1.5rem 0.5rem;
			color: ${colors.black};
		}

		.p-unselectable-text:not(.p-tabview-selected) {
			.p-tabview-nav-link {
				border-bottom: 2px solid ${colors.transparent} !important;
			}
		}

		li.homePage-tab-get-started {
			display: flex;

			.p-tabview-nav-link {
				margin-top: 0;
				padding-block: 0;
				border-bottom-color: ${colors.onboardingPrimary};

				:hover {
					background-color: transparent !important;
				}
			}
		}
	}
`;

const TopMobileMenuBar = styled.div`
	${row(undefined, "spacebetween", "center")};
	position: sticky;
	top: 0;
	height: 80px;
	padding: 0 24px;
	background: ${colors.white};
	z-index: 22;
	border-bottom: 1px solid ${colors.primary25};

	@media ${physioMeMobileBreakpoint} {
		padding-right: 0;
	}
`;

const BottomMobileMenuBar = styled.div`
	${row(undefined, "spacearound", "center")};
	position: sticky;
	bottom: 0;
	width: 100%;

	padding: 8px 24px;
	background: ${colors.white};
	z-index: 99;
	border-top: 1px solid ${colors.primary25};
`;

const BottomMobileMenuTabContainer = styled.div<{ disabled?: boolean }>`
	${stack(undefined, "flexend", "center")}
	position: relative;
	width: 104px;
	opacity: ${({ disabled }) => (disabled ? "0.5" : "1")};
	cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
`;
